import React, { useEffect, useRef, useState } from 'react';
import { Input } from '@ver-uds/uswds-react';
import Button from '../Button';

export interface PaginationGoToPageProps {
  pageCount: number;
  gotoPage: (pageIndex: number) => void;
  pageIndex: number;
  goToPageFormId: string;
}

export default function PaginationGoToPage({
  goToPageFormId,
  gotoPage,
  pageCount,
  pageIndex,
}: PaginationGoToPageProps): JSX.Element {
  const [inputPageNumber, setInputPageNumber] = useState(String(pageIndex + 1));
  useEffect(() => {
    if (pageIndex) {
      setInputPageNumber(String(pageIndex + 1));
    }
  }, [pageIndex]);
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <form
      className="ver-pagination__go-to-page"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        const parsedPageNumber = parseInt(String(inputPageNumber), 10);
        const newPageNumber = parsedPageNumber - 1;
        const parsedMatchesInput = String(parsedPageNumber) === inputPageNumber;
        // If input is empty or pasted an invalid number, refocus input
        if (Number.isNaN(newPageNumber) || !parsedMatchesInput) {
          setInputPageNumber('');
          // Reset the form for edge cases where the the form value is not correctly tracked
          // like "e", "E", or "-"
          // https://github.com/facebook/react/issues/16554#issuecomment-524495473
          (event.target as HTMLFormElement).reset();
          inputRef.current?.focus();
          return;
        }
        // Check lower bounds
        if (newPageNumber < 0) {
          setInputPageNumber('1');
          gotoPage(0);
          return;
        }
        // Check upper bounds
        if (newPageNumber + 1 > pageCount) {
          setInputPageNumber(String(pageCount));
          gotoPage(pageCount - 1);
          return;
        }
        gotoPage(newPageNumber);
      }}
    >
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label className="ver-pagination__go-to-page-label" htmlFor={goToPageFormId}>
        Go to page
      </label>
      <Input
        id={goToPageFormId}
        name={goToPageFormId}
        type="number"
        className="ver-pagination__go-to-page-input"
        value={inputPageNumber}
        onChange={(event) => setInputPageNumber(event.target.value)}
        ref={inputRef}
      />
      <Button type="submit" className="ver-pagination__go-to-page-submit" aria-label="Go to page">
        Go
      </Button>
    </form>
  );
}
